/**
 * Overview group-card slot helpers (FE-5b) — pure logic extracted from overview.route.js.
 */

import { DEFAULT_REMOTE_SIREN_MS, OFFLINE_MS } from "./constants.js";

/**
 * Grantee fleet rows: route/filter by ``shared_group_key`` (share SoT), else ``notification_group``.
 *
 * @param {object | null | undefined} d
 * @param {(v: unknown) => string} canonicalGroupKey
 * @returns {string}
 */
export function deviceSharedGroupKey(d, canonicalGroupKey) {
  const sgk = String((d && d.shared_group_key) || "").trim();
  if (sgk) return canonicalGroupKey(sgk);
  return canonicalGroupKey(d && d.notification_group);
}

/**
 * @param {object | null | undefined} d
 * @returns {boolean}
 */
export function isOverviewDeviceOnline(d) {
  if (!d || typeof d !== "object") return false;
  if (typeof d.is_online === "boolean") return d.is_online;
  return Date.now() - Date.parse(d.updated_at || 0) < OFFLINE_MS;
}

/**
 * @param {object[]} devices
 * @param {string} groupKey
 * @param {string} tenantOwner
 * @param {{ me?: object | null, canonicalGroupKey: (v: unknown) => string }} ctx
 * @returns {string[]}
 */
export function groupDeviceIdsFromList(devices, groupKey, tenantOwner, ctx) {
  const key = ctx.canonicalGroupKey(groupKey);
  if (!key) return [];
  const t = String(tenantOwner || "").trim();
  const isSuper = !!(ctx.me && ctx.me.role === "superadmin");
  const out = [];
  const list = Array.isArray(devices) ? devices : [];
  for (const d of list) {
    if (!d || deviceSharedGroupKey(d, ctx.canonicalGroupKey) !== key) continue;
    if (isSuper) {
      const o = String(d.owner_admin || "").trim();
      if (t === "") {
        if (o) continue;
      } else if (o !== t) {
        continue;
      }
    } else if (t) {
      const devOwner = String(d.owner_admin || "").trim() || (d.is_shared ? String(d.shared_by || "").trim() : "");
      if (devOwner !== t) continue;
    }
    const did = String(d.device_id || "").trim();
    if (did) out.push(did);
  }
  return out;
}

/**
 * @param {object[]} devices
 * @param {string} groupKey
 * @param {(v: unknown) => string} canonicalGroupKey
 * @returns {string}
 */
export function inferUniqueOwnerForGroup(devices, groupKey, canonicalGroupKey) {
  const g = canonicalGroupKey(String(groupKey || ""));
  if (!g) return "";
  const owners = new Set();
  for (const d of Array.isArray(devices) ? devices : []) {
    if (deviceSharedGroupKey(d, canonicalGroupKey) !== g) continue;
    const o = String(d.owner_admin || "").trim();
    if (o) owners.add(o);
  }
  if (owners.size === 1) return [...owners][0];
  return "";
}

/**
 * @param {{ groupKey: string, tenantOwner?: string, metaKey?: string }} slot
 * @param {object[]} devices
 * @param {Map<string, object>} byId
 * @param {{ me?: object | null, canonicalGroupKey: (v: unknown) => string }} ctx
 * @returns {string[]}
 */
export function groupDeviceIdsFromSlot(slot, devices, byId, ctx) {
  const ids = groupDeviceIdsFromList(devices, slot.groupKey, slot.tenantOwner, ctx);
  return ids.filter((x) => byId.has(String(x)));
}

/**
 * Server slot total when overview provides ``notification_group_slots`` (CAP-API-1a).
 *
 * @param {{ serverCounts?: { total?: unknown } } | null | undefined} slot
 * @returns {number | null}
 */
export function overviewSlotServerTotal(slot) {
  if (!slot || !slot.serverCounts) return null;
  const t = Number(slot.serverCounts.total);
  return Number.isFinite(t) ? t : null;
}

/**
 * Loaded device rows for a card slot from the client fleet sample.
 *
 * @param {{ groupKey: string, tenantOwner?: string, metaKey?: string }} slot
 * @param {object[]} devices
 * @param {Map<string, object>} byId
 * @param {{ me?: object | null, canonicalGroupKey: (v: unknown) => string }} ctx
 * @returns {number}
 */
export function overviewSlotLoadedDeviceCount(slot, devices, byId, ctx) {
  return groupDeviceIdsFromSlot(slot, devices, byId, ctx).length;
}

/**
 * True when server-reported group size exceeds devices loaded client-side (DEF-CAP-API-1a-005).
 *
 * @param {{ groupKey: string, tenantOwner?: string, metaKey?: string, serverCounts?: { total?: unknown } }} slot
 * @param {object[]} devices
 * @param {Map<string, object>} byId
 * @param {{ me?: object | null, canonicalGroupKey: (v: unknown) => string }} ctx
 * @returns {boolean}
 */
export function overviewSlotDeviceSampleIncomplete(slot, devices, byId, ctx) {
  const serverTotal = overviewSlotServerTotal(slot);
  if (serverTotal == null) return false;
  return overviewSlotLoadedDeviceCount(slot, devices, byId, ctx) < serverTotal;
}

/** @returns {string} */
export function overviewSlotSampleIncompleteAlarmTitle() {
  return "Overview loaded a partial board sample — open the group page to alarm every board in this group";
}

/**
 * @param {{ groupKey: string, tenantOwner?: string, metaKey?: string }} slot
 * @param {object[]} devices
 * @param {Map<string, object>} byId
 * @param {boolean} canAlert
 * @param {{ me?: object | null, canonicalGroupKey: (v: unknown) => string }} ctx
 * @returns {string[]}
 */
export function sirenTargetIdsForSlot(slot, devices, byId, canAlert, ctx) {
  const ids = groupDeviceIdsFromSlot(slot, devices, byId, ctx);
  if (canAlert) return ids;
  return ids.filter((id) => {
    const d = byId.get(String(id));
    return !!(d && d.can_operate);
  });
}

/**
 * @param {{ groupKey: string, tenantOwner?: string, metaKey?: string }} slot
 * @param {object[]} devices
 * @param {Map<string, object>} byId
 * @param {(cap: string) => boolean} canFn
 * @param {{ me?: object | null, canonicalGroupKey: (v: unknown) => string }} ctx
 * @returns {boolean}
 */
export function canGroupSirenForSlot(slot, devices, byId, canFn, ctx) {
  const serverTotal = overviewSlotServerTotal(slot);
  if (canFn("can_alert") && serverTotal != null && serverTotal > 0) {
    return true;
  }
  return canFn("can_alert") || sirenTargetIdsForSlot(slot, devices, byId, canFn("can_alert"), ctx).length > 0;
}

/**
 * @param {{ groupKey: string, tenantOwner?: string, metaKey?: string }} slot
 * @param {object[]} devices
 * @param {Map<string, object>} byId
 * @param {{ me?: object | null, canonicalGroupKey: (v: unknown) => string }} ctx
 * @returns {string[]}
 */
export function groupSharedBySlot(slot, devices, byId, ctx) {
  const rows = groupDeviceIdsFromSlot(slot, devices, byId, ctx).map((id) => byId.get(String(id))).filter(Boolean);
  const sharedFrom = new Set(rows.map((d) => String(d.shared_by || "")).filter(Boolean));
  return Array.from(sharedFrom);
}

/**
 * @param {object[]} devices
 * @param {string} groupKey
 * @param {(v: unknown) => string} canonicalGroupKey
 * @returns {string[]}
 */
export function groupSharedByNotificationKey(devices, groupKey, canonicalGroupKey) {
  const key = canonicalGroupKey(groupKey);
  if (!key) return [];
  const rows = (Array.isArray(devices) ? devices : []).filter((d) => canonicalGroupKey(d.notification_group) === key);
  const sharedFrom = new Set(rows.map((d) => String(d.shared_by || "")).filter(Boolean));
  return Array.from(sharedFrom);
}

/**
 * @param {string[]} deviceIds
 * @param {object[]} shareItems
 * @returns {Set<string>}
 */
export function granteesFullyCoveringDevices(deviceIds, shareItems) {
  const ids = (Array.isArray(deviceIds) ? deviceIds : []).map((x) => String(x || "").trim()).filter(Boolean);
  const n = ids.length;
  if (!n || !Array.isArray(shareItems)) return new Set();
  const dset = new Set(ids);
  const counts = new Map();
  for (const it of shareItems) {
    if (it && it.revoked_at) continue;
    const did = String((it && it.device_id) || "").trim();
    if (!dset.has(did)) continue;
    const g = String((it && it.grantee_username) || "").trim();
    if (!g) continue;
    counts.set(g, (counts.get(g) || 0) + 1);
  }
  const out = new Set();
  for (const [g, c] of counts) {
    if (c >= n) out.add(g);
  }
  return out;
}

/**
 * @param {object[]} rows
 * @param {(s: string) => string} escapeHtml
 * @returns {string}
 */
export function shareScopeBadgesHtml(rows, escapeHtml) {
  const list = Array.isArray(rows) ? rows.filter(Boolean) : [];
  const n = list.length;
  if (!n) return "";
  const sharedRows = list.filter((d) => d && d.is_shared);
  const sn = sharedRows.length;
  if (sn === 0) return "";
  if (sn === n) {
    const owners = [...new Set(sharedRows.map((d) => String(d.shared_by || "").trim()).filter(Boolean))];
    const o = owners.length === 1 ? owners[0] : owners.join(", ");
    return `<span class="badge accent" title="Board share: every board on this card is shared with you">Shared: whole group · ${escapeHtml(o || "?")}</span>`;
  }
  return `<span class="badge partial" title="Board share: only some boards on this card are shared">Shared: some boards (${sn}/${n})</span>`;
}

/**
 * @param {{ groupKey: string, tenantOwner?: string, metaKey?: string }} slot
 * @param {object[]} devices
 * @param {Map<string, object>} byId
 * @param {{ me?: object | null, canonicalGroupKey: (v: unknown) => string }} ctx
 * @returns {boolean}
 */
export function canEditSharedGroupAlarmSettings(slot, devices, byId, ctx) {
  const ids = groupDeviceIdsFromSlot(slot, devices, byId, ctx);
  const rows = ids.map((id) => byId.get(String(id))).filter(Boolean);
  if (!rows.some((d) => d && d.is_shared)) return true;
  return rows.every((d) => d && d.can_operate);
}

/**
 * @param {object} gs
 * @param {number} [defaultSirenMs]
 * @returns {string}
 */
export function humanizeGroupAlarmLine(gs, defaultSirenMs = DEFAULT_REMOTE_SIREN_MS) {
  const delaySec = Math.max(0, Math.floor(Number(gs.delay_seconds || 0)));
  const durMin = Math.round((Number(gs.trigger_duration_ms) || defaultSirenMs) / 60000 * 10) / 10;
  const parts = [`Siren up to ${durMin} min`];
  if (delaySec > 0) {
    parts.push(`${delaySec}s start delay`);
  }
  if (gs.reboot_self_check) {
    parts.push("then reboot + self-test");
  }
  return parts.join(" · ");
}
